import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { useState, useEffect, FormEvent } from "react";
import { Dialog, IconButton } from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import {
  getAuth,
  updatePassword,
  EmailAuthProvider,
  reauthenticateWithCredential,
  sendPasswordResetEmail,
} from "firebase/auth";
import { useDialog } from "app/hook";
import { RootState } from "app/store";
import { DialogType } from "app/types";
import { parentsFirebase } from "app/Auth";
import { useScale, usePlaySound } from "common/utils";
import { ParentCloseBtn, FadeTransition } from "common/elements";
import I18n from "features/parent/i18n";

type ChangePasswordView = "form" | "reset" | "sent";

interface ChangePasswordModalProps {
  open: boolean;
  onClose: () => void;
}

export const ChangePasswordModal = ({ open, onClose }: ChangePasswordModalProps) => {
  const play = usePlaySound();
  const { scale } = useScale();
  const handleDialog = useDialog();
  const parent = useSelector((state: RootState) => state.user.parent);
  const [view, setView] = useState<ChangePasswordView>("form");
  const [showPassword, setShowPassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newPasswordConfirm, setNewPasswordConfirm] = useState("");
  const [currentPasswordError, setCurrentPasswordError] = useState(false);
  const [newPasswordConfirmError, setNewPasswordConfirmError] = useState(false);
  const [disable, setDisable] = useState(false);

  useEffect(() => {
    if (open) {
      setView("form");
      setCurrentPassword("");
      setNewPassword("");
      setNewPasswordConfirm("");
      setCurrentPasswordError(false);
      setNewPasswordConfirmError(false);
      setShowPassword(false);
      setDisable(false);
    }
  }, [open]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (disable) return;

    play();

    if (!currentPassword || !newPassword || !newPasswordConfirm) {
      return;
    }

    if (newPassword !== newPasswordConfirm) {
      setNewPasswordConfirmError(true);
      return;
    }

    setDisable(true);

    try {
      const currentUser = getAuth(parentsFirebase).currentUser;
      const credential = EmailAuthProvider.credential(currentUser.email, currentPassword);
      handleDialog({ type: DialogType.LOADING, value: true });
      await reauthenticateWithCredential(currentUser, credential);
      await updatePassword(currentUser, newPassword);
      toast(
        <div>
          <p className="text text-textcolor-black">
            {I18n.t("MSG_SIGN_IN_CHANGE_PASSWORD_SUCCESS_CONTENT")}
          </p>
        </div>,
      );
      handleDialog({ type: DialogType.LOADING, value: false });
      onClose();
    } catch (error) {
      console.error(error);
      handleDialog({ type: DialogType.LOADING, value: false });
      setDisable(false);
      if (error?.code === "auth/wrong-password" || error?.code === "auth/invalid-credential") {
        setCurrentPasswordError(true);
      }
    }
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handlePasswordReset = async () => {
    play();
    setDisable(true);
    const auth = getAuth(parentsFirebase);
    try {
      handleDialog({ type: DialogType.LOADING, value: true });
      await sendPasswordResetEmail(auth, parent.email);
      handleDialog({ type: DialogType.LOADING, value: false });
      setView("sent");
    } catch (error) {
      console.error("Password reset error:", error);
      handleDialog({ type: DialogType.LOADING, value: false });
      toast.error(I18n.t("MSG_SIGN_PASSWORD_RESET_FAILED"));
      setDisable(false);
    }
  };

  const isFormValid =
    currentPassword.length > 0 &&
    newPassword.length >= 6 &&
    newPasswordConfirm.length > 0 &&
    !currentPasswordError &&
    !newPasswordConfirmError;

  return (
    <Dialog
      fullScreen
      open={open}
      maxWidth={false}
      sx={{ zIndex: 99999 }}
      TransitionComponent={FadeTransition}
      PaperProps={{ style: { backgroundColor: "transparent" } }}
    >
      <div className="flex-col-center w-full h-full bg-textcolor-black/50">
        <div style={{ transform: `scale(${scale})` }}>
          <div className="flex-col-el w-[600px] border-solid border-gray2-30 border-[16px] rounded-[16px] bg-gray2-30">
            <div className="flex-col-el justify-start w-full h-full relative p-6 rounded-[8px] bg-white">
              <ParentCloseBtn close={onClose} />
              <div className="flex-row-el items-center gap-2 mb-4 pb-3 border-b border-outline shrink-0">
                <span className="material-symbols-outlined !text-[54px] text-red-100">lock</span>
                <h2 className="text-[33px] text-textcolor-black">
                  {I18n.t("MSG_PARENT_PAGE_CHANGE_PASSWORD")}
                </h2>
              </div>

              <div className="flex-col-el w-full h-[450px]">
                {/* フォーム画面 */}
                {view === "form" && (
                  <form
                    className="flex-col-el w-full h-full justify-between"
                    onSubmit={handleSubmit}
                  >
                    {/* コンテンツエリア */}
                    <div className="flex-col-el w-full gap-3">
                      {/* 現在のパスワード */}
                      <div className="flex-col-el relative">
                        <input
                          type={showPassword ? "text" : "password"}
                          value={currentPassword}
                          onChange={(e) => {
                            setCurrentPassword(e.target.value);
                            setCurrentPasswordError(false);
                          }}
                          className={`input-form !bg-white text-body text-textcolor-black text-[30px] pr-12 ${
                            currentPasswordError && "!border-danger"
                          }`}
                          placeholder={I18n.t("MSG_SIGN_CHANGE_PASSWORD_LABEL_CURRENT_PASSWORD")}
                          autoComplete="current-password"
                        />
                        <div className="flex-col-el absolute right-0 top-0 h-[56px] justify-center pr-2">
                          <IconButton onClick={handleClickShowPassword} size="small">
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </div>
                        <label
                          className={`text-body text-left text-[21px] h-5 text-danger ${
                            !currentPasswordError && "invisible"
                          }`}
                        >
                          {I18n.t("MSG_SIGN_IN_CHANGE_PASSWORD_CURRENT_PASSWORD_WRONG")}
                        </label>
                      </div>

                      {/* 新しいパスワード */}
                      <div className="flex-col-el relative">
                        <input
                          type={showPassword ? "text" : "password"}
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          className="input-form !bg-white text-body text-textcolor-black text-[30px] pr-12"
                          placeholder={I18n.t("MSG_SIGN_CHANGE_PASSWORD_LABEL_NEW_PASSWORD")}
                          autoComplete="new-password"
                          minLength={6}
                        />
                        <div className="flex-col-el absolute right-0 top-0 h-[56px] justify-center pr-2">
                          <IconButton onClick={handleClickShowPassword} size="small">
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </div>
                        <label
                          className={`text-body text-left text-[21px] h-5 text-danger ${
                            newPassword.length === 0 || newPassword.length >= 6 ? "invisible" : ""
                          }`}
                        >
                          {I18n.t("MSG_APP_ERROR_PASSWORD_TOO_SHORT")}
                        </label>
                      </div>

                      {/* 新しいパスワード（確認） */}
                      <div className="flex-col-el relative">
                        <input
                          type={showPassword ? "text" : "password"}
                          value={newPasswordConfirm}
                          onChange={(e) => {
                            setNewPasswordConfirm(e.target.value);
                            setNewPasswordConfirmError(false);
                          }}
                          className={`input-form !bg-white text-body text-textcolor-black text-[30px] pr-12 ${
                            newPasswordConfirmError && "!border-danger"
                          }`}
                          placeholder={I18n.t(
                            "MSG_SIGN_CHANGE_PASSWORD_LABEL_NEW_PASSWORD_CONFIRM",
                          )}
                          autoComplete="new-password"
                        />
                        <div className="flex-col-el absolute right-0 top-0 h-[56px] justify-center pr-2">
                          <IconButton onClick={handleClickShowPassword} size="small">
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </div>
                        <label
                          className={`text-body text-left text-[21px] h-5 text-danger ${
                            !newPasswordConfirmError && "invisible"
                          }`}
                        >
                          {I18n.t("MSG_APP_ERROR_PASSWORD_CONFIRM_NOT_VALID")}
                        </label>
                      </div>
                    </div>

                    {/* ボタンエリア（下部固定） */}
                    <div className="flex-col-el items-center gap-6">
                      <div className="flex-row-el flex-center gap-4">
                        <button
                          type="button"
                          onClick={onClose}
                          className="flex-row-el items-center justify-center w-[160px] py-3 bg-white border border-gray2-60 text-textcolor-black rounded-[12px] text-[24px] cursor-pointer hover:bg-gray2-20 transition-all"
                        >
                          {I18n.t("MSG_CANCEL_BTN_BY_PARENT")}
                        </button>
                        <button
                          type="submit"
                          disabled={!isFormValid}
                          className="flex-row-el items-center justify-center w-[160px] py-3 bg-ink text-white border-none rounded-[12px] text-[24px] cursor-pointer hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {I18n.t("MSG_CONFIRM_BTN_BY_PARENT")}
                        </button>
                      </div>
                      {/* パスワードを忘れた場合のリンク */}
                      <button
                        type="button"
                        onClick={() => {
                          play();
                          setView("reset");
                        }}
                        className="text-[21px] text-blue-100 underline cursor-pointer hover:opacity-80 transition-all bg-transparent border-none"
                      >
                        {I18n.t("MSG_SIGN_IN_FORGOT_PASSWORD")}
                      </button>
                    </div>
                  </form>
                )}

                {/* リセット確認画面 */}
                {view === "reset" && (
                  <div className="flex-col-el w-full h-full justify-between">
                    {/* コンテンツエリア */}
                    <div className="flex-col-el w-full gap-4">
                      <p className="text-[24px] text-textcolor-black text-center">
                        {I18n.t("MSG_SIGN_PASSWORD_RESET_DESCRIPTION")}
                      </p>
                      <div className="flex-col-el items-center gap-2 p-4 bg-gray2-20 rounded-[12px]">
                        <span className="material-symbols-outlined !text-[36px] text-gray2-100">
                          email
                        </span>
                        <p className="text-[24px] text-textcolor-black">{parent?.email}</p>
                      </div>
                    </div>
                    {/* ボタンエリア（下部固定） */}
                    <div className="flex-row-el flex-center gap-4">
                      <button
                        type="button"
                        onClick={() => {
                          play();
                          setView("form");
                        }}
                        className="flex-row-el items-center justify-center w-[160px] py-3 bg-white border border-gray2-60 text-textcolor-black rounded-[12px] text-[24px] cursor-pointer hover:bg-gray2-20 transition-all"
                      >
                        {I18n.t("MSG_BACK_BTN_BY_PARENT")}
                      </button>
                      <button
                        type="button"
                        onClick={handlePasswordReset}
                        disabled={disable}
                        className="flex-row-el items-center justify-center w-auto px-6 py-3 bg-ink text-white border-none rounded-[12px] text-[24px] whitespace-nowrap cursor-pointer hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {I18n.t("MSG_SIGN_PASSWORD_RESET_SEND_BTN")}
                      </button>
                    </div>
                  </div>
                )}

                {/* 送信完了画面 */}
                {view === "sent" && (
                  <div className="flex-col-el w-full h-full justify-between items-center">
                    {/* コンテンツエリア */}
                    <div className="flex-col-el w-full gap-4 items-center">
                      <span className="material-symbols-outlined !text-[72px] text-green-100">
                        check_circle
                      </span>
                      <p className="text-[24px] text-textcolor-black text-center">
                        {I18n.t("MSG_SIGN_PASSWORD_RESET_SENT")}
                      </p>
                      <p className="text-[21px] text-gray2-100 text-center">{parent?.email}</p>
                    </div>
                    {/* ボタンエリア（下部固定） */}
                    <div className="flex-row-el flex-center">
                      <button
                        type="button"
                        onClick={onClose}
                        className="flex-row-el items-center justify-center w-[160px] py-3 bg-ink text-white border-none rounded-[12px] text-[24px] cursor-pointer hover:opacity-90 transition-all"
                      >
                        {I18n.t("MSG_CLOSE_BTN_BY_PARENT")}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
};
